import {
  useSourceTransferAssignments,
  useSupplierAssignments,
  type AssignmentLine,
  type CartAssignment,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import {
  sourceTransferAssignmentsInput,
  supplierAssignmentsInput,
  type CartLinesQuery,
} from './supplierAssignmentsInput';

export interface CartLines {
  /** The vendor's or the source location's name. */
  ownerName: string;
  carts: CartAssignment[];
  lines: AssignmentLine[];
}

/**
 * One owner's carts and lines: a vendor's from the supplier assignments, a
 * source location's from the transfer assignments. Only the owner's query runs.
 */
export function useCartLines(
  query: CartLinesQuery,
  enabled: boolean,
  readCached = false,
) {
  const { owner } = query;
  const supplier = useSupplierAssignments(
    supplierAssignmentsInput({
      ...query,
      supplierId: owner.kind === 'po' ? owner.supplierId : '',
    }),
    enabled && owner.kind === 'po',
    readCached,
  );
  const source = useSourceTransferAssignments(
    sourceTransferAssignmentsInput(
      query,
      owner.kind === 'to' ? owner.sourceWarehouseId : '',
    ),
    enabled && owner.kind === 'to',
    readCached,
  );
  if (owner.kind === 'po') {
    const { data } = supplier;
    return {
      data: data && {
        ownerName: data.supplierName,
        carts: data.carts,
        lines: data.lines,
      },
      isLoading: supplier.isLoading,
      isFetching: supplier.isFetching,
    };
  }
  const { data } = source;
  return {
    data: data && {
      ownerName: data.sourceWarehouseName,
      carts: data.carts,
      lines: data.lines,
    },
    isLoading: source.isLoading,
    isFetching: source.isFetching,
  };
}
